import React, { useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "../assets/images/Logo.png";
import { TbEye, TbEyeOff } from "react-icons/tb";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";
import CryptoJS from "crypto-js";
import { ShieldCheck, Fingerprint, BadgeCheck, ArrowRight, Lock, User, Mail, Phone, Hash } from "lucide-react";
import { BsWhatsapp } from "react-icons/bs";

import { config } from "../config/config.jsx";

const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

function encryptData(data) {
  return CryptoJS.AES.encrypt(JSON.stringify(data), SECRET_KEY).toString();
}

const inputBase = "w-full pl-4 pr-10 py-3 rounded-xl border text-sm text-slate-800 outline-none transition-all focus:ring-2 focus:ring-indigo-400 bg-white";
const inputState = (invalid, filled) =>
  invalid ? "border-red-400 bg-red-50" : filled ? "border-emerald-400" : "border-slate-200";

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Detect initial mode from URL: /signup → register, else login
  const initMode = location.pathname === "/signup" ? "register" : "login";
  const [mode, setMode] = useState(initMode);
  const [signupStep, setSignupStep] = useState(1);

  // ----- Login State -----
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [loginInvalid, setLoginInvalid] = useState({});
  const [showLoginPw, setShowLoginPw] = useState(false);

  // ----- Register State -----
  const [regForm, setRegForm] = useState({
    Firstname: "", Lastname: "", email: "",
    phone_num: "", NIN: "", password: "", terms: false,
  });
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState("");
  const [regInvalid, setRegInvalid] = useState({});
  const [showRegPw, setShowRegPw] = useState(false);

  // ---- Switch mode ----
  const switchMode = (m) => {
    setMode(m);
    setLoginError(""); setRegError("");
    setLoginInvalid({}); setRegInvalid({});
    setSignupStep(1);
    navigate(m === "register" ? "/signup" : "/login", { replace: true });
  };

  // ---- Login handlers ----
  const handleLoginChange = (e) => {
    const { name, value } = e.target;
    setLoginForm(p => ({ ...p, [name]: value }));
    setLoginInvalid(p => ({ ...p, [name]: false }));
  };

  const validateLogin = () => {
    const inv = {};
    if (!loginForm.email || !/\S+@\S+\.\S+/.test(loginForm.email)) inv.email = true;
    if (!loginForm.password || loginForm.password.length < 8) inv.password = true;
    setLoginInvalid(inv);
    return Object.keys(inv).length === 0;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    if (!validateLogin()) { setLoginError("Please correct the highlighted fields."); return; }
    setLoginLoading(true);
    try {
      const res = await axios.post(
        `${config.apiBaseUrl}${config.endpoints.login}`,
        { email: loginForm.email, password: loginForm.password },
        { withCredentials: true }
      );
      localStorage.setItem("user", encryptData(res.data));
      localStorage.setItem("showWelcomeModal", "true");
      toast.success("Login successful! Redirecting...");
      setTimeout(() => navigate("/dashboard"), 1500);
    } catch (err) {
      const msg = err.response?.data?.message || "Login failed. Please try again.";
      setLoginError(msg);
      toast.error(msg);
    }
    setLoginLoading(false);
  };

  // ---- Register handlers ----
  const handleRegChange = (e) => {
    const { name, value, type, checked } = e.target;
    setRegForm(p => ({ ...p, [name]: type === "checkbox" ? checked : value }));
    setRegInvalid(p => ({ ...p, [name]: false }));
  };

  const validateStep1 = () => {
    const inv = {};
    if (!regForm.Firstname) inv.Firstname = true;
    if (!regForm.Lastname) inv.Lastname = true;
    if (!regForm.email || !/\S+@\S+\.\S+/.test(regForm.email)) inv.email = true;
    setRegInvalid(inv);
    return Object.keys(inv).length === 0;
  };

  const validateStep2 = () => {
    const inv = {};
    if (!regForm.phone_num || regForm.phone_num.length < 7) inv.phone_num = true;
    if (!regForm.NIN || regForm.NIN.length < 8) inv.NIN = true;
    if (!regForm.password || regForm.password.length < 8) inv.password = true;
    if (!regForm.terms) inv.terms = true;
    setRegInvalid(inv);
    return Object.keys(inv).length === 0;
  };

  const handleNext = (e) => {
    e.preventDefault();
    setRegError("");
    if (validateStep1()) setSignupStep(2);
    else setRegError("Please fill in all required fields.");
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegError("");
    if (!validateStep2()) { setRegError("Please fill in all required fields."); return; }
    setRegLoading(true);
    try {
      await axios.post(`${config.apiBaseUrl}${config.endpoints.register}`, {
        firstName: regForm.Firstname, lastName: regForm.Lastname,
        email: regForm.email, phone: regForm.phone_num,
        nin: regForm.NIN, password: regForm.password,
      });
      Swal.fire({ icon: "success", title: "Signup Successful!", text: "You will be redirected to login.", timer: 2000, showConfirmButton: false });
      setRegForm({ Firstname: "", Lastname: "", email: "", phone_num: "", NIN: "", password: "", terms: false });
      setSignupStep(1);
      setTimeout(() => switchMode("login"), 2100);
    } catch (err) {
      const msg = err.response?.data?.message || "Signup failed. Please try again.";
      setRegError(msg);
      if (msg === "User already exists") toast.error("User already exists. Please log in.");
    }
    setRegLoading(false);
  };

  const trustBadges = [
    { icon: ShieldCheck, text: "Bank-Grade Encryption" },
    { icon: Fingerprint, text: "Biometric Protected" },
    { icon: BadgeCheck, text: "NDPR Compliant" },
  ];

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="min-h-screen flex bg-white">

        {/* ─── Left Panel: Form ─── */}
        <div className="flex-1 flex flex-col justify-center items-center px-6 py-12 relative bg-white border-r border-slate-100">

          {/* Top nav */}
          <div className="absolute top-6 left-6">
            <NavLink to="/" className="flex items-center gap-2">
              <img src={Logo} alt="Fylder" className="h-9 w-auto object-contain" />
              <span className="text-xl font-extrabold text-[#0a0040] tracking-tight">Fylder</span>
            </NavLink>
          </div>

          <div className="w-full max-w-md">

            {/* Tab Toggle */}
            <div className="flex bg-slate-100 rounded-xl p-1 mb-8">
              <button
                onClick={() => switchMode("login")}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
                  mode === "login"
                    ? "bg-white text-[#0a0040] shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Log In
              </button>
              <button
                onClick={() => switchMode("register")}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all cursor-pointer ${
                  mode === "register"
                    ? "bg-white text-[#0a0040] shadow-sm"
                    : "text-slate-500 hover:text-slate-700"
                }`}
              >
                Create Account
              </button>
            </div>

            <AnimatePresence mode="wait">
              {mode === "login" ? (
                <motion.div
                  key="login"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.25 }}
                >
                  <h2 className="text-2xl font-extrabold text-[#0a0040] mb-1">Welcome back</h2>
                  <p className="text-sm text-slate-500 mb-6">Sign in to your Fylder account</p>

                  {loginError && (
                    <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">
                      {loginError}
                      {(loginError.toLowerCase().includes("password")) && (
                        <NavLink to="/forgottenpassword" className="ml-2 font-bold underline text-red-500">Reset password</NavLink>
                      )}
                    </div>
                  )}

                  <form onSubmit={handleLogin} className="flex flex-col gap-4">
                    {/* Email */}
                    <div>
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Email</label>
                      <div className="relative">
                        <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="email" name="email" id="login-email"
                          value={loginForm.email} onChange={handleLoginChange}
                          placeholder="you@example.com"
                          className={`${inputBase} pl-9 ${inputState(loginInvalid.email, loginForm.email)}`}
                        />
                      </div>
                    </div>

                    {/* Password */}
                    <div>
                      <div className="flex justify-between mb-1.5">
                        <label className="text-xs font-bold text-slate-500 uppercase tracking-wider">Password</label>
                        <NavLink to="/forgottenpassword" className="text-xs text-indigo-600 font-semibold hover:underline">Forgot password?</NavLink>
                      </div>
                      <div className="relative">
                        <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type={showLoginPw ? "text" : "password"} name="password" id="login-password"
                          value={loginForm.password} onChange={handleLoginChange}
                          placeholder="Min. 8 characters"
                          className={`${inputBase} pl-9 pr-10 ${inputState(loginInvalid.password, loginForm.password)}`}
                        />
                        <button type="button" onClick={() => setShowLoginPw(p => !p)} tabIndex={-1}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 cursor-pointer hover:text-slate-600">
                          {showLoginPw ? <TbEyeOff size={18} /> : <TbEye size={18} />}
                        </button>
                      </div>
                    </div>

                    <button
                      type="submit" disabled={loginLoading}
                      className="mt-2 flex items-center justify-center gap-2 w-full py-3.5 bg-[#0a0040] hover:bg-indigo-900 text-white rounded-xl text-sm font-bold shadow-lg shadow-indigo-900/20 transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer disabled:opacity-60"
                    >
                      {loginLoading ? (
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg>
                      ) : null}
                      {loginLoading ? "Signing in..." : "Sign In"} {!loginLoading && <ArrowRight size={15} />}
                    </button>
                  </form>

                  <p className="mt-6 text-center text-sm text-slate-500">
                    New to Fylder?{" "}
                    <button onClick={() => switchMode("register")} className="text-indigo-600 font-bold hover:underline cursor-pointer">Create an account</button>
                  </p>
                </motion.div>
              ) : (
                <motion.div
                  key="register"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.25 }}
                >
                  {/* Step indicators */}
                  <div className="flex items-center gap-2 mb-6">
                    <div className={`h-2 rounded-full transition-all duration-300 ${signupStep === 1 ? "w-8 bg-[#0a0040]" : "w-4 bg-indigo-200"}`} />
                    <div className={`h-2 rounded-full transition-all duration-300 ${signupStep === 2 ? "w-8 bg-[#0a0040]" : "w-4 bg-indigo-200"}`} />
                    <span className="text-xs text-slate-400 ml-2">Step {signupStep} of 2</span>
                  </div>

                  <h2 className="text-2xl font-extrabold text-[#0a0040] mb-1">
                    {signupStep === 1 ? "Create your account" : "Secure your account"}
                  </h2>
                  <p className="text-sm text-slate-500 mb-6">
                    {signupStep === 1 ? "Start with your personal information" : "Add your NIN, phone & set a password"}
                  </p>

                  {regError && (
                    <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{regError}</div>
                  )}

                  <form onSubmit={signupStep === 1 ? handleNext : handleRegister} className="flex flex-col gap-4">
                    {signupStep === 1 ? (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">First Name</label>
                            <div className="relative">
                              <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                              <input type="text" name="Firstname" id="Firstname"
                                value={regForm.Firstname} onChange={handleRegChange} placeholder="John"
                                className={`${inputBase} pl-9 ${inputState(regInvalid.Firstname, regForm.Firstname)}`} />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Last Name</label>
                            <div className="relative">
                              <User size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                              <input type="text" name="Lastname" id="Lastname"
                                value={regForm.Lastname} onChange={handleRegChange} placeholder="Doe"
                                className={`${inputBase} pl-9 ${inputState(regInvalid.Lastname, regForm.Lastname)}`} />
                            </div>
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Email</label>
                          <div className="relative">
                            <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input type="email" name="email" id="reg-email"
                              value={regForm.email} onChange={handleRegChange} placeholder="you@example.com"
                              className={`${inputBase} pl-9 ${inputState(regInvalid.email, regForm.email)}`} />
                          </div>
                        </div>
                        <button type="submit"
                          className="mt-2 flex items-center justify-center gap-2 w-full py-3.5 bg-[#0a0040] hover:bg-indigo-900 text-white rounded-xl text-sm font-bold shadow-lg shadow-indigo-900/20 transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer">
                          Continue <ArrowRight size={15} />
                        </button>
                      </>
                    ) : (
                      <>
                        <div>
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Phone Number</label>
                          <div className="relative">
                            <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input type="tel" name="phone_num" id="Phone_Num"
                              value={regForm.phone_num} onChange={handleRegChange} placeholder="+234 800 000 0000"
                              className={`${inputBase} pl-9 ${inputState(regInvalid.phone_num, regForm.phone_num)}`} />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">NIN Number</label>
                          <div className="relative">
                            <Hash size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input type="number" name="NIN" id="NIN"
                              value={regForm.NIN} onChange={handleRegChange} placeholder="11-digit NIN"
                              className={`${inputBase} pl-9 ${inputState(regInvalid.NIN, regForm.NIN)}`} />
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">Password</label>
                          <div className="relative">
                            <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input type={showRegPw ? "text" : "password"} name="password" id="reg-password"
                              value={regForm.password} onChange={handleRegChange} placeholder="Min. 8 characters"
                              className={`${inputBase} pl-9 pr-10 ${inputState(regInvalid.password, regForm.password)}`} />
                            <button type="button" onClick={() => setShowRegPw(p => !p)} tabIndex={-1}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 cursor-pointer hover:text-slate-600">
                              {showRegPw ? <TbEyeOff size={18} /> : <TbEye size={18} />}
                            </button>
                          </div>
                        </div>
                        <div className={`flex items-start gap-2.5 p-3 rounded-xl border ${regInvalid.terms ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-200"}`}>
                          <input type="checkbox" id="terms" name="terms"
                            checked={regForm.terms} onChange={handleRegChange}
                            className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-indigo-600 cursor-pointer" />
                          <label htmlFor="terms" className="text-xs text-slate-600 leading-relaxed cursor-pointer">
                            I agree to Fylder's <a href="#" className="text-indigo-600 font-bold hover:underline">Terms of Service</a> and consent to processing my identity data securely.
                          </label>
                        </div>
                        <div className="flex gap-3">
                          <button type="button" onClick={() => setSignupStep(1)} disabled={regLoading}
                            className="flex-1 py-3.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-bold hover:bg-slate-50 transition-all cursor-pointer">
                            Back
                          </button>
                          <button type="submit" disabled={regLoading}
                            className="flex-[2] flex items-center justify-center gap-2 py-3.5 bg-[#0a0040] hover:bg-indigo-900 text-white rounded-xl text-sm font-bold shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer disabled:opacity-60">
                            {regLoading ? <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" /></svg> : null}
                            {regLoading ? "Creating..." : "Create Account"}
                          </button>
                        </div>
                      </>
                    )}
                  </form>

                  <p className="mt-6 text-center text-sm text-slate-500">
                    Already have an account?{" "}
                    <button onClick={() => switchMode("login")} className="text-indigo-600 font-bold hover:underline cursor-pointer">Sign In</button>
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* ─── Right Panel: Brand Visual ─── */}
        <div className="hidden lg:flex flex-col w-[44%] bg-[#0a0040] relative overflow-hidden">

          <div className="relative flex flex-col justify-center h-full px-12 py-16 z-10">
            {/* Headline */}
            <div className="mb-10">
              <span className="text-xs font-bold text-indigo-300 uppercase tracking-widest mb-3 block">Identity Verification Platform</span>
              <h2 className="text-4xl font-black text-white leading-tight">
                Verify identities<br /> with confidence.
              </h2>
              <p className="text-sm text-indigo-200 mt-4 leading-relaxed max-w-sm">
                Access NIN, BVN, and IPE clearance services through one secure, consent-driven platform trusted by businesses across Nigeria.
              </p>
            </div>

            {/* Trust Badges */}
            <div className="flex flex-col gap-3 mb-10">
              {trustBadges.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-3">
                  <div className="h-8 w-8 rounded-lg border border-white/20 text-slate-300 flex items-center justify-center shrink-0">
                    <Icon size={16} />
                  </div>
                  <span className="text-sm text-slate-300">{text}</span>
                </div>
              ))}
            </div>

            {/* Verification card */}
            <div className="border border-white/15 rounded-xl p-5 max-w-xs">
              <div className="flex items-center gap-3 mb-3">
                <div className="h-8 w-8 rounded-lg border border-emerald-500/40 text-emerald-400 flex items-center justify-center">
                  <BadgeCheck size={16} />
                </div>
                <div>
                  <div className="text-xs font-bold text-white">NIN Verified</div>
                  <div className="text-[10px] text-slate-400">Just now · Biometric match confirmed</div>
                </div>
              </div>
              <div className="h-px w-full bg-white/10 my-3" />
              <div className="flex items-center justify-between">
                <span className="text-[11px] text-slate-400">Confidence Score</span>
                <div className="flex items-center gap-2">
                  <div className="h-1.5 w-24 bg-white/10 rounded-full overflow-hidden">
                    <div className="h-full w-[94%] bg-emerald-500 rounded-full" />
                  </div>
                  <span className="text-[11px] font-bold text-emerald-400">94%</span>
                </div>
              </div>
            </div>

            {/* WhatsApp support link */}
            <a href="https://wa.me/2349077968959" target="_blank" rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 text-sm text-green-400 hover:text-green-300 transition-colors font-semibold">
              <BsWhatsapp size={16} /> Need help? Chat with us on WhatsApp
            </a>
          </div>
        </div>

      </div>
    </>
  );
};

export default AuthPage;
